/**
 * Shared utilities for MCP config converters
 */

import type { McpServerConfig } from '../types';

/**
 * Checks if an object is a valid MCP server configuration
 * A valid config must have either:
 * - command (for STDIO transport)
 * - url (for HTTP/SSE transport)
 */
export const isValidServerConfig = (obj: unknown): obj is McpServerConfig => {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const config = obj as Record<string, unknown>;

  // Must have either command or url
  const hasCommand = typeof config.command === 'string' && config.command.length > 0;
  const hasUrl = typeof config.url === 'string' && config.url.length > 0;

  return hasCommand || hasUrl;
};

/**
 * Checks if an object looks like it could be a server name -> config mapping
 * (i.e., all values are valid server configs)
 */
export const isServerNameConfigMapping = (obj: unknown): boolean => {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return false;
  }

  const entries = Object.entries(obj as Record<string, unknown>);
  if (entries.length === 0) {
    return false;
  }

  // All values must be valid server configs
  return entries.every(([key, value]) => {
    // Key should be a reasonable server name (not a config property)
    const isReasonableName = !['command', 'url', 'args', 'env', 'type', 'headers'].includes(key);
    return isReasonableName && isValidServerConfig(value);
  });
};

/**
 * Normalizes a server config to ensure consistent structure
 */
export const normalizeConfig = (config: McpServerConfig): McpServerConfig => {
  const normalized: McpServerConfig = { ...config };

  // Ensure type is set based on transport
  if (!normalized.type) {
    if (normalized.command) {
      normalized.type = 'stdio';
    } else if (normalized.url) {
      // Check if URL suggests SSE
      if (normalized.url.includes('/sse') || normalized.url.endsWith('/sse')) {
        normalized.type = 'sse';
      } else {
        normalized.type = 'http';
      }
    }
  }

  return normalized;
};
